import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Icon } from "../../components/icons";
import { Pagination } from "../../components/Pagination";
import { SelectionBar } from "../../components/SelectionBar";
import { Alert, Button, Card, EmptyState, Input, Mono, Select, Table, TableSkeleton, Td, Th, Tag, cx } from "../../components/ui";
import { formatDate, formatDateTime, formatRelative } from "../../lib/format";
import { useBulkReviewFleetDomains, useBulkSyncFleetDomains, useFleetDomains, useReviewFleetDomain, useSyncFleetDomain } from "../../lib/queries";
import { useDomainsTab } from "./DomainsPage";
import type { FleetDomain, FleetDomainParams, FleetDomainSyncResult, RegisteredDomainStatus } from "../../lib/types";

const LIMIT = 25;

const STATUS_TONE: Record<RegisteredDomainStatus, "neutral" | "good" | "warn" | "bad"> = {
  pending: "warn",
  active: "good",
  failed: "bad",
  expired: "bad",
  deleted: "neutral",
};

type Filter = RegisteredDomainStatus | "" | "flagged" | "renewalFailing" | "expiring";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "", label: "All" },
  { key: "flagged", label: "Flagged" },
  { key: "renewalFailing", label: "Renewal failing" },
  { key: "expiring", label: "Expiring soon" },
  { key: "active", label: "Active" },
  { key: "pending", label: "Pending" },
  { key: "expired", label: "Expired" },
  { key: "failed", label: "Failed" },
];

const SPECIAL: Filter[] = ["flagged", "renewalFailing", "expiring"];

/** Under this many days to expiry, the date is worth colour. */
const EXPIRY_WARN_DAYS = 30;

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

/**
 * The page's rows as a CSV, for the spreadsheet a finance or abuse question
 * ends up in. Client-side from what is on screen: the operator has already
 * filtered to what they mean, and a server export would be a second thing
 * to keep in step with the list.
 */
function toCsv(rows: FleetDomain[]): string {
  const cell = (v: string | number | boolean | null | undefined) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["domain", "organization", "status", "registered", "expires", "autorenew", "whois_privacy", "locked", "dns", "registrar_id", "flags"];
  const lines = rows.map((d) =>
    [d.domain, d.organization.name, d.status, d.registeredAt ?? "", d.expiresAt ?? "", d.autorenew, d.privateWhois, d.locked, d.managedDns ? "hosted" : "elsewhere", d.openproviderId ?? "", d.abuseFlags.join(" ")]
      .map(cell)
      .join(","),
  );
  return [head.join(","), ...lines].join("\n");
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Every name the platform holds, for the support conversation that starts
 * "my domain". A row opens into the facts a ticket needs — the registrar's
 * id and status, the dates, the settings, the nameservers — and a button
 * that re-reads the registrar, so the answer is current rather than the
 * last sweep's.
 */
export function DomainsTab() {
  const { canReview } = useDomainsTab();
  const [params] = useSearchParams();
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState<Filter>(
    params.get("flagged") ? "flagged" : params.get("renewalFailing") ? "renewalFailing" : params.get("expiring") ? "expiring" : "",
  );
  const [sort, setSort] = useState<NonNullable<FleetDomainParams["sort"]>>(params.get("expiring") ? "expiry" : "newest");
  const [q, setQ] = useState(params.get("q") ?? "");
  const [open, setOpen] = useState<string | null>(null);
  const domains = useFleetDomains({
    limit: LIMIT,
    offset,
    status: status && !SPECIAL.includes(status) ? (status as RegisteredDomainStatus) : undefined,
    flagged: status === "flagged" || undefined,
    renewalFailing: status === "renewalFailing" || undefined,
    expiring: status === "expiring" || undefined,
    sort,
    q: q || undefined,
  });
  const rows = domains.data?.data ?? [];
  const total = domains.data?.total ?? 0;

  /*
   * Selection is per page and by id, so a name that scrolled off is not
   * silently acted on. Both bulk actions take at most a page, which is the
   * registrar's pace; the count in the bar says what is about to happen.
   */
  const [selected, setSelected] = useState<Map<string, FleetDomain>>(new Map());
  const [outcome, setOutcome] = useState<{ text: string; failures: FleetDomainSyncResult[] } | null>(null);
  const bulkSync = useBulkSyncFleetDomains();
  const bulkReview = useBulkReviewFleetDomains();
  const pageIds = rows.map((d) => d.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const chosen = [...selected.values()];
  const flaggedChosen = chosen.filter((d) => d.abuseFlags.length > 0).length;
  const toggle = (domain: FleetDomain) =>
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(domain.id)) next.delete(domain.id);
      else next.set(domain.id, domain);
      return next;
    });

  const runSync = () =>
    bulkSync.mutate([...selected.keys()], {
      onSuccess: (result) => {
        const failures = result.data.filter((r) => !r.ok);
        setOutcome({ text: `Checked ${result.synced} of ${result.data.length} with the registrar.`, failures });
        setSelected(new Map());
      },
    });
  const runReview = () =>
    bulkReview.mutate([...selected.keys()], {
      onSuccess: (result) => {
        setOutcome({ text: `Marked ${result.reviewed} name${result.reviewed === 1 ? "" : "s"} as reviewed.`, failures: [] });
        setSelected(new Map());
      },
    });

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              aria-pressed={status === filter.key}
              onClick={() => {
                setStatus(filter.key);
                setOffset(0);
              }}
              className={cx(
                "rounded-md px-2.5 py-1.5 text-[13px] transition-colors duration-200 ease-smooth",
                status === filter.key ? "bg-fg/8 font-medium text-fg" : "text-fg-muted hover:bg-fg/5 hover:text-fg",
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="w-full sm:ml-auto sm:w-72">
          <Input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOffset(0);
            }}
            placeholder="Search by name or organization"
          />
        </div>
        <Select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} aria-label="Sort" className="w-auto">
          <option value="newest">Newest first</option>
          <option value="expiry">Expiry, soonest</option>
          <option value="name">Name</option>
        </Select>
        <span className="text-[13px] text-fg-subtle">
          {total} domain{total === 1 ? "" : "s"}
        </span>
        {rows.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => download(`domains-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(chosen.length > 0 ? chosen : rows))}>
            <Icon name="archive" className="size-3.5" />
            {chosen.length > 0 ? `Export ${chosen.length}` : "Export page"}
          </Button>
        )}
      </div>

      {domains.error && <Alert>{domains.error.message}</Alert>}
      {bulkSync.error && <Alert>{bulkSync.error.message}</Alert>}
      {bulkReview.error && <Alert>{bulkReview.error.message}</Alert>}
      {outcome && (
        <Alert tone={outcome.failures.length > 0 ? "warning" : "success"}>
          {outcome.text}
          {outcome.failures.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {outcome.failures.map((f) => (
                <li key={f.id}>
                  <Mono>{f.fqdn}</Mono>: {f.error}
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}
      {domains.isPending ? (
        <TableSkeleton columns={5} />
      ) : rows.length === 0 ? (
        <EmptyState title="No domains" description={q || status ? "Nothing matches." : "No customer has registered one yet."} />
      ) : (
        <Table
          head={
            <>
              {canReview && (
                <Th className="w-8">
                  <input
                    type="checkbox"
                    aria-label="Select every domain on this page"
                    checked={allOnPage}
                    onChange={() =>
                      setSelected((current) => {
                        const next = new Map(current);
                        if (allOnPage) for (const id of pageIds) next.delete(id);
                        else for (const d of rows) next.set(d.id, d);
                        return next;
                      })
                    }
                    className="h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                  />
                </Th>
              )}
              <Th>Domain</Th>
              <Th>Organization</Th>
              <Th priority="sm">Status</Th>
              <Th priority="md">Expires</Th>
              <Th priority="lg">DNS</Th>
              <Th />
            </>
          }
        >
          {rows.map((domain) => (
            <DomainRow
              key={domain.id}
              domain={domain}
              open={open === domain.id}
              onToggle={() => setOpen(open === domain.id ? null : domain.id)}
              selectable={canReview}
              selected={selected.has(domain.id)}
              onSelect={() => toggle(domain)}
            />
          ))}
        </Table>
      )}
      <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />

      <SelectionBar
        count={selected.size}
        noun="domain"
        onClear={() => setSelected(new Map())}
        action={
          <>
            {flaggedChosen > 0 && (
              <Button size="sm" variant="secondary" loading={bulkReview.isPending} onClick={runReview}>
                Mark {flaggedChosen} reviewed
              </Button>
            )}
            <Button size="sm" loading={bulkSync.isPending} onClick={runSync}>
              <Icon name="refresh" className="size-3.5" />
              Check with registrar
            </Button>
          </>
        }
      >
        {selected.size > LIMIT ? `Up to ${LIMIT} at a time.` : "One registrar call per name."}
      </SelectionBar>
    </Card>
  );
}

function DomainRow({
  domain,
  open,
  onToggle,
  selectable,
  selected,
  onSelect,
}: {
  domain: FleetDomain;
  open: boolean;
  onToggle: () => void;
  selectable: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const { canReview } = useDomainsTab();
  const sync = useSyncFleetDomain();
  const review = useReviewFleetDomain();
  const needsEye = domain.failureReason || domain.renewalError;
  const days = domain.status === "active" ? daysUntil(domain.expiresAt) : null;
  return (
    <>
      <tr className={cx("cursor-pointer transition-colors hover:bg-raised/50", (open || selected) && "bg-raised/40")} onClick={onToggle}>
        {selectable && (
          <Td>
            <input
              type="checkbox"
              aria-label={`Select ${domain.domain}`}
              checked={selected}
              onChange={onSelect}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
            />
          </Td>
        )}
        <Td>
          <Mono className="text-fg">{domain.domain}</Mono>
          {domain.kind === "transfer" && domain.status === "pending" && <div className="mt-0.5 text-[11px] text-fg-subtle">transfer in</div>}
          {needsEye && <div className="mt-0.5 text-[11px] text-danger">{domain.failureReason ?? domain.renewalError}</div>}
          {domain.abuseFlags.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {domain.abuseFlags.map((flag) => (
                <Tag key={flag} tone="warn">{flag}</Tag>
              ))}
            </div>
          )}
        </Td>
        <Td>
          <Link to={`/organizations/${domain.organization.id}`} className="text-fg hover:underline" onClick={(e) => e.stopPropagation()}>
            {domain.organization.name}
          </Link>
        </Td>
        <Td priority="sm">
          <Tag tone={STATUS_TONE[domain.status]}>{domain.status}</Tag>
        </Td>
        <Td priority="md">
          {domain.expiresAt ? (
            <span className={cx(days !== null && days <= EXPIRY_WARN_DAYS && (domain.autorenew ? "text-warn" : "text-danger"))}>
              {formatDate(domain.expiresAt)}
              {days !== null && days <= EXPIRY_WARN_DAYS && (
                <span className="ml-1 text-[11px]">{days <= 0 ? "today" : `in ${days}d`}</span>
              )}
            </span>
          ) : (
            <span className="text-fg-subtle">—</span>
          )}
          {domain.status === "active" && !domain.autorenew && <div className="text-[11px] text-fg-subtle">autorenew off</div>}
          {domain.status === "active" && domain.autorenew && domain.renewalDate && (
            <div className="text-[11px] text-fg-subtle">renews {formatDate(domain.renewalDate)}</div>
          )}
        </Td>
        <Td priority="lg">{domain.managedDns ? "hosted" : "elsewhere"}</Td>
        <Td className="text-right">
          <Icon name="chevron" className={cx("inline size-3.5 text-fg-subtle transition-transform", open && "rotate-90")} />
        </Td>
      </tr>
      {open && (
        <tr>
          <td colSpan={selectable ? 7 : 6} className="bg-raised/30 px-5 py-4">
            <div className="grid gap-x-8 gap-y-3 text-[13px] sm:grid-cols-2 lg:grid-cols-4">
              <Fact label="Registrar" value={domain.openproviderId ? `#${domain.openproviderId} · ${domain.registrarStatus ?? "—"}` : "no record"} mono />
              <Fact label="Registered" value={domain.registeredAt ? formatDate(domain.registeredAt) : "—"} />
              <Fact label="Expires" value={domain.expiresAt ? formatDate(domain.expiresAt) : "—"} />
              <Fact label="Last synced" value={domain.lastSyncedAt ? formatRelative(domain.lastSyncedAt) : "never"} />
              <Fact label="Autorenew" value={domain.autorenew ? "on" : "off"} />
              <Fact label="WHOIS privacy" value={domain.privateWhois ? "on" : "off"} />
              <Fact label="Transfer lock" value={domain.locked ? "on" : "off"} />
              <Fact label="Nameservers" value={domain.managedDns ? "hosted here" : domain.nameServers.join(", ") || "—"} mono />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                loading={sync.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  sync.mutate(domain.id);
                }}
              >
                <Icon name="refresh" className="size-3.5" />
                Check with registrar
              </Button>
              {canReview && domain.abuseFlags.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  loading={review.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    review.mutate(domain.id);
                  }}
                >
                  <Icon name="check" className="size-3.5" />
                  Mark reviewed
                </Button>
              )}
              <span className="text-[12px] text-fg-subtle" title={domain.createdAt ? formatDateTime(domain.createdAt) : undefined}>
                ordered {formatRelative(domain.createdAt)}
              </span>
              {sync.error && <span className="text-[12px] text-danger">{sync.error.message}</span>}
              {review.error && <span className="text-[12px] text-danger">{review.error.message}</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="micro text-fg-subtle">{label}</p>
      <p className={cx("mt-0.5 text-fg", mono && "font-mono text-[12px]")}>{value}</p>
    </div>
  );
}
